import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import './quotes.css';
import { useUrlState } from '../nav/urlState';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln } from '../catalog/money';
import { useClientNames } from '../events/EventsPage';
import { EVENTS_KEY, EVENTS_RESOURCE, formatEventRange, isClosed, type EventView } from '../events/labels';
import { stateText } from './VersionBar';
import { LINES_RESOURCE, QUOTES_KEY, QUOTES_RESOURCE, TOTALS_RESOURCE, type QuoteListRow, type VersionState } from './types';
import { downloadPdf, PdfPreviewDialog } from './PdfPreviewDialog';
import { refreshEvents } from '../events/refresh';
import { canStartQuote, NewQuoteDialog } from './NewQuoteDialog';
import { RowMenu, type RowAction } from '../ui/RowMenu';
import { DeleteQuoteDialog } from './DeleteQuoteDialog';
import { rangeFromParams, rangeLabel, shiftRange, wholeMonth, type ListRange } from './listRange';
import { todayInWarsaw } from '../events/calendar';
import { DateField } from '../events/EventFields';
import { OffersSection, QuotesViewSwitch } from './offers/OffersSection';

type Filter = '' | 'todo' | VersionState;
const FILTERS: { id: Filter; label: string }[] = [
  { id: '', label: 'Wszystkie' },
  { id: 'todo', label: 'Do zrobienia' },
  { id: 'robocza', label: 'Robocze' },
  { id: 'wyslana', label: 'Wysłane' },
  { id: 'zaakceptowana', label: 'Zaakceptowane' },
  { id: 'odrzucona', label: 'Odrzucone' },
];


/**
 * Owner 2026-10-22 00:20 ("z pozycji wyceny przechodź do formy pełnego widoku"): from "Wyceny" the quote opens on the
 * card's tab "Kosztorys" over the whole window (`pelny=1`, so "Wstecz / Dalej" and links keep it); other ways to the
 * quote keep the panel beside the calendar.
 */
export const quoteFromListUrl = (eventId: string): string => `/wydarzenia?wydarzenie=${eventId}&zakladka=kosztorys&pelny=1`;
/** One row of "Wyceny": an event's quote, or an event flagged "potrzebna wycena" that has none yet. */
export type WorkRow = {
  key: string;
  eventId: string;
  eventName: string;
  eventDeleted: boolean;
  startsAt: string | null;
  endsAt: string | null;
  clientName: string;
  quote: QuoteListRow | null;
  /** Flagged "potrzebna wycena" and no quote yet (or only a draft): marked and pinned on top. */
  todo: boolean;
  flagged: EventView | null;
};

const fold = (s: string) => s.normalize('NFD').replace(/\p{M}+/gu, '').replace(/ł/g, 'l').replace(/Ł/g, 'L').toLowerCase();

/**
 * The rows of "Wyceny" (owner's remark 25): every quote plus every flagged event without one; to-do rows first
 * (nearest date first), then the rest by date, newest first.
 */
export function workRows(quotes: QuoteListRow[], flagged: EventView[], clientNames: Map<string, string>, othersEventIds: readonly string[] = []): WorkRow[] {
  const byEvent = new Map(flagged.map((e) => [e.id, e]));
  const rows: WorkRow[] = quotes.map((q) => {
    const f = byEvent.get(q.eventId) ?? null;
    return {
      key: q.quoteId,
      eventId: q.eventId,
      eventName: q.eventName,
      eventDeleted: q.eventDeleted,
      startsAt: q.startsAt,
      endsAt: q.endsAt,
      clientName: q.clientName,
      quote: q,
      todo: f !== null && !isClosed(f.status) && q.state === 'robocza',
      flagged: f,
    };
  });
  // P16: an event whose quote someone else holds is not "to do" for a user who sees only own quotes
  const quoted = new Set([...quotes.map((q) => q.eventId), ...othersEventIds]);
  for (const e of flagged) {
    if (quoted.has(e.id) || isClosed(e.status)) continue;
    rows.push({
      key: `event-${e.id}`,
      eventId: e.id,
      eventName: e.name ?? 'Wydarzenie',
      eventDeleted: false,
      startsAt: e.startsAt ?? null,
      endsAt: e.endsAt ?? null,
      clientName: e.contractorId ? (clientNames.get(e.contractorId) ?? '') : '',
      quote: null,
      todo: true,
      flagged: e,
    });
  }
  const at = (r: WorkRow) => (r.startsAt ? Date.parse(r.startsAt) : 0);
  return rows.sort((a, b) => Number(b.todo) - Number(a.todo) || (a.todo ? at(a) - at(b) : at(b) - at(a)) || a.key.localeCompare(b.key));
}

/**
 * Menu "Wyceny": quotes done and to do in one list (owner's remark 25). Events flagged "potrzebna wycena" without
 * a sent quote are marked and on top; every row opens the event's quote. Net and gross only for a role that may
 * see the totals.
 */
export function QuotesPage() {
  const { me } = useMe();
  const queryClient = useQueryClient();
  // P13: the filter and the search live in the address ("Wstecz" comes back to them)
  const { params, update } = useUrlState();
  const filterParam = params.get('filtr') ?? '';
  const filter: Filter = FILTERS.find((f) => f.id === filterParam)?.id ?? '';
  const setFilter = (f: Filter) => update({ filtr: f });
  const search = params.get('szukaj') ?? '';
  const setSearch = (v: string) => update({ szukaj: v });
  // owner 2026-10-24: the period (current month unless the address says otherwise) and "Pokaż ukryte"
  const range = rangeFromParams(params);
  const setRange = (r: ListRange | null) => update({ od: r?.from ?? null, do: r?.to ?? null });
  const showHidden = params.get('ukryte') === '1';
  const [removing, setRemoving] = useState<QuoteListRow | null>(null);
  const [preview, setPreview] = useState<{ versionId: string; number: number } | null>(null);
  const [starting, setStarting] = useState(false);
  const navigate = useNavigate();
  const names = useClientNames();
  const seeQuotes = !!me && can(me, QUOTES_RESOURCE, 'view');
  const seeEvents = !!me && can(me, EVENTS_RESOURCE, 'view');
  const period = `from=${range.from}&to=${range.to}`;
  const quotes = useQuery({
    queryKey: [...QUOTES_KEY, 'list', range.from, range.to, showHidden],
    enabled: seeQuotes,
    queryFn: () => api<{ quotes: QuoteListRow[]; othersEventIds?: string[]; hiddenEventIds?: string[] }>(`/api/quotes?${period}${showHidden ? '&hidden=1' : ''}`),
  });
  const flagged = useQuery({ queryKey: [...EVENTS_KEY, 'quotes-todo', range.from, range.to], enabled: seeEvents, queryFn: () => api<{ events: EventView[] }>(`/api/events?needsQuote=true&${period}`) });
  const hide = useMutation({
    mutationFn: (v: { quoteId: string; hidden: boolean }) => api(`/api/quotes/quotes/${v.quoteId}/hidden`, { method: 'PUT', body: { hidden: v.hidden } }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUOTES_KEY }),
  });
  const done = useMutation({
    mutationFn: (e: EventView) => api<EventView>(`/api/events/${e.id}`, { method: 'PATCH', body: { version: e.version, needsQuote: false } }),
    onSettled: () => refreshEvents(queryClient),
  });
  if (!me) return null;
  // „Oferta łączona” (owner 2026-10-30): the second view of „Wyceny”
  const offersView = params.get('oferty') === '1';
  const switchView = (offers: boolean) => update({ oferty: offers ? '1' : null });
  if (offersView && seeQuotes) return <OffersSection me={me} onSwitch={switchView} />;
  const showTotals = seeQuotes && fieldVisible(me, TOTALS_RESOURCE, 'totals');
  // the PDF shows every amount: every price field (as "Pobierz PDF" on the quote)
  const showPdf = showTotals && fieldVisible(me, TOTALS_RESOURCE, 'discountBp') && ['unitPriceGrosze', 'discountBp', 'netGrosze'].every((f) => fieldVisible(me, LINES_RESOURCE, f));
  const all = workRows(quotes.data?.quotes ?? [], flagged.data?.events ?? [], names, [...(quotes.data?.othersEventIds ?? []), ...(quotes.data?.hiddenEventIds ?? [])]);
  const wanted = fold(search.trim());
  const rows = all.filter((r) => (filter === '' ? true : filter === 'todo' ? r.todo : r.quote?.state === filter) && (wanted === '' || fold(r.eventName).includes(wanted)));
  const loading = quotes.isLoading || flagged.isLoading;
  const error = [quotes.error, flagged.error, done.error, hide.error].find((e) => e) ?? null;
  // "Ukryj na liście": edit on quotes and the field `hidden` (rule 7a); "Usuń wycenę…": delete on quotes
  const mayHide = seeQuotes && can(me, QUOTES_RESOURCE, 'edit') && fieldVisible(me, QUOTES_RESOURCE, 'hidden');
  const mayDelete = seeQuotes && can(me, QUOTES_RESOURCE, 'delete');
  const month = wholeMonth(range);
  const thisMonth = todayInWarsaw().slice(0, 7);
  const rowActions = (r: WorkRow): RowAction[] => {
    const q = r.quote;
    if (!q) return [];
    const actions: RowAction[] = [];
    if (mayHide && !q.hidden) actions.push({ label: 'Ukryj na liście', onSelect: () => hide.mutate({ quoteId: q.quoteId, hidden: true }) });
    if (mayDelete) actions.push({ label: 'Usuń wycenę…', danger: true, onSelect: () => setRemoving(q) });
    return actions;
  };
  const todoCount = all.filter((r) => r.todo).length;

  return (
    <section className="page wide quotes-page" data-feature-id="quotes.list">
      <div className="page-head">
        <h1>Wyceny</h1>
        <input type="search" aria-label="Szukaj wydarzenia" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Szukaj: nazwa wydarzenia" autoComplete="off" />
        {canStartQuote(me) && (
          <button className="btn primary" type="button" data-feature-id="quotes.new_with_event" onClick={() => setStarting(true)}>
            Nowa wycena
          </button>
        )}
      </div>
      {seeQuotes && <QuotesViewSwitch offers={false} onChange={switchView} />}
      <p className="lede">
        {todoCount > 0 ? `Do zrobienia: ${todoCount}. ` : ''}Wyceny wydarzeń z wybranego okresu: do zrobienia na górze, dalej od najnowszego terminu. Kliknij wydarzenie, żeby otworzyć jego wycenę.
      </p>
      <div className="q-period" role="group" aria-label="Okres" data-feature-id="quotes.list_tidy">
        <div className="segment">
          <button type="button" onClick={() => setRange(shiftRange(range, -1))}>
            Poprzedni miesiąc
          </button>
          <button type="button" aria-pressed={month === thisMonth} onClick={() => setRange(null)}>
            Bieżący miesiąc
          </button>
          <button type="button" onClick={() => setRange(shiftRange(range, 1))}>
            Następny miesiąc
          </button>
        </div>
        <div className="q-period-days">
          <DateField label="Od" value={range.from} onChange={(day) => setRange({ from: day, to: day > range.to ? day : range.to })} />
          <DateField label="Do" value={range.to} onChange={(day) => setRange({ from: day < range.from ? day : range.from, to: day })} />
        </div>
        <strong className="q-period-label" aria-live="polite">
          {rangeLabel(range)}
        </strong>
        {mayHide && (
          <label className="q-show-hidden">
            <input type="checkbox" checked={showHidden} onChange={(e) => update({ ukryte: e.target.checked ? '1' : null })} />
            Pokaż ukryte
          </label>
        )}
      </div>
      <div className="segment q-filters" role="group" aria-label="Pokaż">
        {FILTERS.map((f) => (
          <button key={f.id || 'all'} type="button" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
            {f.label}
          </button>
        ))}
      </div>
      {loading && <p className="muted" role="status">Wczytywanie…</p>}
      {error && <p className="alert" role="alert">{error instanceof ApiError ? error.message : 'Nie udało się wczytać wycen'}</p>}
      {!loading && rows.length === 0 && <p className="muted">{filter || wanted ? 'Nic nie pasuje do wybranych filtrów.' : `Brak wycen w okresie: ${rangeLabel(range)}. Zmień okres albo zacznij od „Nowa wycena”.`}</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table quotes-table" aria-label="Wyceny">
            <thead>
              <tr>
                <th scope="col">Wydarzenie</th>
                <th scope="col">Termin</th>
                <th scope="col" className="wide-only">Klient</th>
                <th scope="col">Stan</th>
                {showTotals && <th scope="col" className="num wide-only">Netto</th>}
                {showTotals && <th scope="col" className="num">Brutto</th>}
                <th scope="col">
                  <span className="visually-hidden">Działania</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className={[r.todo ? 'q-todo' : '', r.quote?.hidden ? 'q-hidden' : ''].filter(Boolean).join(' ') || undefined}>
                  <th scope="row">
                    {r.eventDeleted || !seeEvents ? (
                      <span className={r.eventDeleted ? 'muted' : undefined}>{r.eventName}</span>
                    ) : (
                      <Link to={quoteFromListUrl(r.eventId)}>{r.eventName}</Link>
                    )}
                  </th>
                  <td>{r.startsAt && r.endsAt ? formatEventRange(r.startsAt, r.endsAt) : '—'}</td>
                  <td className="wide-only">{r.clientName || '—'}</td>
                  <td>
                    {r.todo && <span className="quote-flag">Potrzebna wycena</span>}{' '}
                    {/* owner 2026-10-24 10:22: the quote of a deleted event is hidden by itself */}
                    {r.quote?.hidden && <span className="q-hidden-mark">{r.eventDeleted ? 'Ukryta · wydarzenie usunięte' : 'Ukryta'}</span>}{' '}
                    {r.quote && (
                      <span className="q-list-state">
                        <span className="q-vnum">v{r.quote.lastVersion}</span>{' '}
                        <span className="q-vstate" data-state={r.quote.state}>
                          {stateText({ state: r.quote.state, outdated: false, sentAt: r.quote.sentAt })}
                        </span>
                        {r.quote.acceptedVersion !== null && r.quote.acceptedVersion !== r.quote.lastVersion && <span className="q-list-also"> · v{r.quote.acceptedVersion} zaakceptowana</span>}
                      </span>
                    )}
                  </td>
                  {showTotals && <td className="num wide-only">{r.quote?.totals ? formatPln(r.quote.totals.netGrosze) : ''}</td>}
                  {showTotals && <td className="num">{r.quote?.totals ? formatPln(r.quote.totals.grossGrosze) : ''}</td>}
                  <td className="tpl-actions">
                    {r.quote && showPdf && (
                      <button className="btn small ghost" type="button" aria-label={`Podgląd PDF: ${r.eventName}`} onClick={() => setPreview({ versionId: r.quote!.lastVersionId, number: r.quote!.lastVersion })} data-feature-id="quotes.pdf_preview">
                        Podgląd PDF
                      </button>
                    )}
                    {r.flagged && can(me, EVENTS_RESOURCE, 'edit') && (
                      <button className="btn small ghost" type="button" aria-label={`Zrobione: ${r.eventName}`} disabled={done.isPending} onClick={() => done.mutate(r.flagged!)}>
                        Zrobione
                      </button>
                    )}
                    {r.quote?.hidden && !r.eventDeleted && mayHide && (
                      <button className="btn small" type="button" aria-label={`Przywróć: ${r.eventName}`} disabled={hide.isPending} onClick={() => hide.mutate({ quoteId: r.quote!.quoteId, hidden: false })}>
                        Przywróć
                      </button>
                    )}
                    <RowMenu label={`Więcej działań: ${r.eventName}`} floating="end" actions={rowActions(r)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {starting && (
        <NewQuoteDialog
          me={me}
          onClose={() => setStarting(false)}
          onCreated={(eventId) => {
            setStarting(false);
            void queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
            void refreshEvents(queryClient);
            navigate(quoteFromListUrl(eventId));
          }}
        />
      )}
      {removing && <DeleteQuoteDialog quote={removing} onClose={() => setRemoving(null)} />}
      {preview && <PdfPreviewDialog versionId={preview.versionId} number={preview.number} onDownload={() => downloadPdf(preview.versionId)} onClose={() => setPreview(null)} />}
    </section>
  );
}
